import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

export const sameEntityId = (a, b) => {
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
};

export const findById = (list, id) => {
  if (id == null) return null;
  return (list || []).find((item) => sameEntityId(item?.id, id)) || null;
};

export const resolveSelectedById = (id, ...lists) => {
  if (id == null) return null;
  for (const list of lists) {
    const found = findById(list, id);
    if (found) return found;
  }
  return null;
};

export const resolveSelectedByIds = (ids, ...lists) => {
  const parsedIds = Array.isArray(ids)
    ? ids
    : String(ids ?? "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

  return parsedIds
    .map((id) => resolveSelectedById(id, ...lists))
    .filter(Boolean);
};

export const mergeEntityOptions = (options, entities = []) => {
  let list = options || [];
  for (const entity of entities) {
    list = mergeEntityOption(list, entity);
  }
  return list;
};

export const resolveSelectedEntity = (value, ...lists) => {
  if (!value) return null;
  const id = typeof value === "object" ? value.id : value;
  const found = resolveSelectedById(id, ...lists);
  const fallback = typeof value === "object" && value.id != null ? value : null;
  if (!found) return fallback;
  if (!fallback) return found;
  return {
    ...found,
    ...(fallback.cpf != null && fallback.cpf !== "" ? { cpf: fallback.cpf } : {}),
    ...(fallback.name ? { name: fallback.name } : {}),
  };
};

export const mergeEntityOption = (options, entity) => {
  if (!entity?.id) return options || [];
  const list = options || [];
  if (findById(list, entity.id)) return list;
  return [entity, ...list];
};

export const parseEntityResults = (data) =>
  Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];

/** CPF fica só com dígitos no banco. Máscara vira dígitos; texto com letra permanece nome. */
export const normalizeCustomerSearchQuery = (query) => {
  const q = String(query || "").trim();
  if (!q) return "";
  if (/[A-Za-zÀ-ÿ]/.test(q)) return q;
  const digits = q.replace(/\D/g, "");
  return digits || q;
};

export const buildCustomerSearchUrl = (query, pageSize = 30) => {
  const params = new URLSearchParams({ page_size: String(pageSize) });
  const q = normalizeCustomerSearchQuery(query);
  if (q.length >= MIN_SEARCH_LENGTH) params.set("contains", q);
  return `/backend/customers/?${params.toString()}`;
};

export const buildBankSearchUrl = (query, pageSize = 30) => {
  const params = new URLSearchParams({ page_size: String(pageSize) });
  const q = String(query || "").trim();
  if (q.length >= MIN_SEARCH_LENGTH) params.set("contains", q);
  return `/backend/banks/?${params.toString()}`;
};

/** Remove entradas duplicadas pelo id. */
export const dedupeEntityOptions = (options) => {
  const seen = new Set();
  return (options || []).filter((item) => {
    if (item?.id == null) return false;
    const key = Number(item.id);
    if (Number.isNaN(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Desativa filtro local — a lista vem filtrada da API. */
export const noLocalFilter = (options) => options;

/** Esconde nomes que não batem com o texto digitado (fallback enquanto a API responde). */
export const createAsyncEntityFilterOptions = (inputValue) => (options) => {
  const deduped = dedupeEntityOptions(options);
  const q = String(inputValue ?? "").trim().toLowerCase();
  if (q.length < MIN_SEARCH_LENGTH) return deduped;
  return deduped.filter((option) =>
    String(option?.name ?? "").toLowerCase().includes(q)
  );
};

export const useAsyncEntitySearch = ({
  buildUrl,
  selectedEntity = null,
  selectedEntities = null,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const listboxKey = 0;
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const latestQueryRef = useRef("");
  const selectedRef = useRef(selectedEntity);
  const selectedEntitiesRef = useRef(selectedEntities);

  selectedRef.current = selectedEntity;
  selectedEntitiesRef.current = selectedEntities;

  const mergeSelected = useCallback((list, query) => {
    const q = String(query ?? "").trim();
    const deduped = dedupeEntityOptions(list);

    // Com busca ativa, a listagem deve refletir só o que bate com o texto digitado.
    if (q.length >= MIN_SEARCH_LENGTH) {
      return deduped;
    }

    const entities = Array.isArray(selectedEntitiesRef.current)
      ? selectedEntitiesRef.current.filter((entity) => entity?.id)
      : selectedRef.current?.id
        ? [selectedRef.current]
        : [];
    return dedupeEntityOptions(mergeEntityOptions(deduped, entities));
  }, []);

  const applyOptions = useCallback(
    (list, query) => {
      if (latestQueryRef.current !== query) return;
      setOptions(mergeSelected(list, query));
    },
    [mergeSelected]
  );

  const search = useCallback(
    (query) => {
      const q = String(query ?? "").trim();
      latestQueryRef.current = q;

      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();

      // Com 1 caractere, aguarda o mínimo de 2 para consultar a API.
      if (q.length === 1) {
        setLoading(false);
        setOptions([]);
        return;
      }

      setLoading(true);

      // Remove resultados de buscas anteriores (ex.: lista completa ao abrir o combo).
      if (q.length >= MIN_SEARCH_LENGTH) {
        setOptions([]);
      }

      const controller = new AbortController();
      abortRef.current = controller;

      timerRef.current = setTimeout(async () => {
        try {
          const res = await fetch(buildUrl(q), {
            credentials: "include",
            signal: controller.signal,
          });
          if (!res.ok) throw new Error("Falha na busca");
          const data = await res.json();
          applyOptions(parseEntityResults(data), q);
        } catch (e) {
          if (e?.name !== "AbortError") {
            applyOptions([], q);
          }
        } finally {
          if (latestQueryRef.current === q) {
            setLoading(false);
          }
        }
      }, q ? SEARCH_DEBOUNCE_MS : 0);
    },
    [applyOptions, buildUrl]
  );

  const selectedEntityIdsKey = useMemo(() => {
    const entities = Array.isArray(selectedEntities)
      ? selectedEntities
      : selectedEntity?.id
        ? [selectedEntity]
        : [];
    return entities
      .map((entity) => entity?.id)
      .filter((id) => id != null)
      .sort((a, b) => Number(a) - Number(b))
      .join(",");
  }, [selectedEntity, selectedEntities]);

  useEffect(() => {
    if (String(latestQueryRef.current).trim().length >= MIN_SEARCH_LENGTH) {
      return;
    }

    const entities = Array.isArray(selectedEntitiesRef.current)
      ? selectedEntitiesRef.current.filter((entity) => entity?.id)
      : selectedRef.current?.id
        ? [selectedRef.current]
        : [];
    if (entities.length) {
      setOptions((prev) =>
        dedupeEntityOptions(mergeEntityOptions(prev, entities))
      );
    }
  }, [selectedEntityIdsKey]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  return { options, loading, search, listboxKey };
};

export const handleAsyncAutocompleteInputChange = (search, reason, inputValue) => {
  if (reason === "input" || reason === "clear") {
    search(inputValue);
  }
};

/** Mantém o texto digitado quando o Autocomplete dispara `reset` ao atualizar options. */
export const useAsyncAutocompleteInputState = () => {
  const [inputValue, setInputValue] = useState("");

  const bindInputChange = useCallback(
    (search) => (event, newInputValue, reason) => {
      if (reason === "input" || reason === "clear") {
        setInputValue(newInputValue);
      }
      handleAsyncAutocompleteInputChange(search, reason, newInputValue);
    },
    []
  );

  const clearInput = useCallback(() => setInputValue(""), []);

  return { inputValue, setInputValue, bindInputChange, clearInput };
};

export const asyncAutocompleteListboxProps = (listboxKey) => ({
  key: String(listboxKey),
});
